import React, { memo, useState, useCallback, HTMLAttributes, useEffect } from 'react';
import InputMask from '@mona-health/react-input-mask';
import styled from 'styled-components';

import { CommonInputProps } from '@front/common/src/components/input/types';
import { getInputColors } from '@front/common/src/components/input/styles';
import type { InputMode, InputType } from '@front/common/src/components/input/types';

import { WithClassNameProps } from '../types';
import { StylelessInput } from '../utils';

import { Container, InputContainer, InputFrame, baseInputStyle, InputStyleProps } from './components';
import AnimatedPlaceholder from './components/animated-placeholder';
import InputIcon from './components/input-icon';
import BottomMessage from './components/bottom-message';

const BaseInput = styled(StylelessInput)<InputStyleProps>`
  ${baseInputStyle}
`;

type InputProps = WithClassNameProps &
  CommonInputProps & {
    ariaLabel?: HTMLAttributes<HTMLInputElement>['aria-label'];
    autoComplete?: string;
    disabled?: boolean;
    iconName?: string;
    id: string;
    inputMode?: InputMode;
    isBorderErrorWithoutErrorDisplay?: boolean;
    label: string;
    // TODO type any while we found a better replacement that '@mona-health/react-input-mask'
    mask?: any;
    max?: string;
    min?: string;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onUnmount?: () => void;
    readOnly?: boolean;
    step?: number;
    type?: InputType;
  };

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      type = 'text',
      name,
      label,
      iconName,
      placeholder,
      error,
      isBorderErrorWithoutErrorDisplay = false,
      message,
      onBlur,
      onFocus,
      onChange,
      autoComplete,
      value,
      inputMode,
      min,
      max,
      step,
      className,
      mask,
      readOnly = false,
      disabled = false,
      ariaLabel,
      onUnmount,
    },
    ref,
  ) => {
    const hasLabel = !!label;
    // value can be a number, 0 is considered a value
    const hasValue = !(value === undefined || value === null || value === '');
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => () => onUnmount?.(), [onUnmount]);

    // We use borderErrorWithoutErrorDisplay because for the input-password component, we want the border
    // to have the error color if there is an error but we don't want the error to appear on the screen.
    const isError = error || isBorderErrorWithoutErrorDisplay;

    const inputColors = getInputColors({ error: isError, hasValue, isFocused });

    const onInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(event);
    };

    const onInputFocus = useCallback(
      // @ts-ignore TODO: set typing
      (event) => {
        setIsFocused(true);
        onFocus?.(event);
      },
      [onFocus],
    );

    const baseInputProps = {
      $hasLabel: hasLabel,
      $isFocused: isFocused,
      'aria-label': ariaLabel ?? label,
      autoComplete,
      disabled,
      error,
      id,
      inputMode,
      label,
      max,
      min,
      name,
      placeholder,
      ref,
      step,
      type,
    };

    const inputProps = {
      onBlur: onInputBlur,
      onFocus: onInputFocus,
      onChange,
      value,
      readOnly,
      disabled,
    };

    return (
      <Container $isDisabled={disabled} className={className}>
        <InputFrame $inputId={id} $hasHoverStyle={!disabled && !isFocused && !error} $inputColors={inputColors}>
          <InputContainer>
            {mask ? (
              <InputMask mask={mask} maskPlaceholder={null} {...inputProps} ref={ref}>
                <BaseInput {...baseInputProps} />
              </InputMask>
            ) : (
              <BaseInput {...baseInputProps} {...inputProps} />
            )}
            {hasLabel && (
              <AnimatedPlaceholder
                label={label}
                inputId={id}
                isActive={isFocused || hasValue}
                color={inputColors.placeholder}
              />
            )}
          </InputContainer>
          {iconName && <InputIcon color={inputColors.icon} inputId={id} name={iconName} />}
        </InputFrame>
        <BottomMessage error={error} message={message} />
      </Container>
    );
  },
);

export type { InputProps };
export default memo(Input);
